<script setup lang="ts">
import {computed, ref} from "vue";
import {CircleAlert, Info, MessageSquare, RefreshCw, Sparkles} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import useAgendaOperators from "@/agenda/composables/useAgendaOperators";
import HealthJourneyStepCard from "@/agenda/components/health-journey/HealthJourneyStepCard.vue";
import HealthJourneySlotPicker from "@/agenda/components/health-journey/HealthJourneySlotPicker.vue";
import {COMBINATION_LABELS, EXCLUSION_LABELS, JourneyAct, MAX_ACTS} from "@/agenda/lib/healthJourney";
import {HealthJourneyProposal, HealthJourneyStep} from "@/agenda/interfaces/healthJourney";

/**
 * The proposed journey, laid out for the pharmacist to accept or bend.
 *
 * Everything the server said about its own answer is on display: what it
 * computed, what it could not, and the rules it had to obey. The worded
 * summary is a convenience on top, so the screen stays readable when the
 * wording was skipped during an edit or fell back on its own sentences.
 */
const {proposal, acts, generating, programming, stale, recapNote} = defineProps<{
    proposal: HealthJourneyProposal;
    acts: JourneyAct[];
    generating: boolean;
    programming: boolean;
    /**
     * Whether the acts asked for have changed since this plan was computed.
     * A recalculation is already on its way; nothing may be written until it
     * lands, and nothing at all if it never does.
     */
    stale: boolean;
    /** What the patient will or will not receive, said before anything is written. */
    recapNote: string;
}>();

const emits = defineEmits<{
    (e: 'pin', moment: {clientRef: string; date: string; start: string; member_id: number | null}): void;
    (e: 'unpin', clientRef: string): void;
    (e: 'remove', clientRef: string): void;
    (e: 'recompute'): void;
    (e: 'submit'): void;
}>();

const {operators} = useAgendaOperators();

const editing = ref<HealthJourneyStep | null>(null);
const pickerOpen = ref(false);

const plan = computed(() => proposal.plan);

const actOf = (step: HealthJourneyStep) => acts.find((act) => act.clientRef === step.client_ref) ?? null;

const labelOf = (step: HealthJourneyStep) => actOf(step)?.label ?? step.name ?? 'Rendez-vous santé';

const memberName = (id: number | null) =>
    id === null ? null : operators.value.find((operator) => operator.accessId === id)?.name ?? 'Collaborateur';

/** The assistant's sentence for an act, keyed the way the summary numbers them. */
const reasonOf = (step: HealthJourneyStep) =>
    proposal.summary?.step_reasons.find((reason) => reason.index === step.index)?.text ?? null;

/** How many acts each visit holds, so a card can say it shares one. */
const visitSizes = computed(() => {
    const sizes = new Map<number, number>();

    plan.value.steps.forEach((step) => sizes.set(step.visit, (sizes.get(step.visit) ?? 0) + 1));

    return sizes;
});

const opensVisit = (step: HealthJourneyStep, index: number) =>
    index === 0 || plan.value.steps[index - 1].visit !== step.visit;

/** Why acts the pharmacy allows to share a visit were nonetheless separated. */
const separations = computed(() => plan.value.combinations
    .filter((combination) => !combination.kept && combination.because)
    .map((combination) => COMBINATION_LABELS[combination.because as string])
    .filter((label): label is string => !!label));

const exclusions = computed(() => Object.entries(plan.value.exclusions)
    .map(([code, count]) => EXCLUSION_LABELS[code] ? `${count} ${EXCLUSION_LABELS[code]}` : null)
    .filter((label): label is string => !!label));

const quietShare = computed(() => plan.value.kpis.quiet_slot_share === null
    ? null
    : Math.round(plan.value.kpis.quiet_slot_share * 100));

/**
 * Nothing may be written while an act has no moment to be written at, nor
 * while the plan answers a question the pharmacist has since changed.
 */
const blocked = computed(() => stale || plan.value.kpis.placed === 0 || plan.value.kpis.acts > MAX_ACTS);

const openPicker = (step: HealthJourneyStep) => {
    editing.value = step;
    pickerOpen.value = true;
};

const pick = (moment: {date: string; start: string; member_id: number | null}) => {
    if (editing.value) {
        emits('pin', {clientRef: editing.value.client_ref, ...moment});
    }
};
</script>

<template>
  <section class="flex min-h-0 flex-1 flex-col gap-3">
    <div class="rounded-lg bg-white p-4 shadow">
      <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div>
          <h2 class="text-sm font-semibold text-primary">
            {{ plan.kpis.acts }} rendez-vous sur {{ plan.kpis.span_weeks }} semaine{{ plan.kpis.span_weeks > 1 ? 's' : '' }}
          </h2>
          <p class="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
            <span>{{ plan.kpis.visits }} visite{{ plan.kpis.visits > 1 ? 's' : '' }}</span>
            <span v-if="plan.kpis.trips_saved">· {{ plan.kpis.trips_saved }} déplacement(s) évité(s)</span>
            <span v-if="quietShare !== null">· {{ quietShare }} % sur des créneaux calmes</span>
            <span v-if="plan.kpis.same_member">· même collaborateur</span>
          </p>
        </div>
        <Button variant="outline" :disabled="generating" class="gap-2" @click="emits('recompute')">
          <RefreshCw class="size-4" :class="{'animate-spin': generating}" />
          Recalculer
        </Button>
      </div>

      <p v-if="proposal.summary" class="mt-2.5 flex items-start gap-1.5 text-sm leading-snug text-gray-dark">
        <Sparkles class="mt-0.5 size-4 shrink-0 text-primary" />
        {{ proposal.summary.text }}
      </p>

      <p
        v-if="proposal.wording_status === 'degraded'"
        class="mt-2 flex items-start gap-1.5 text-xs leading-snug text-gray-dark/70"
      >
        <Info class="mt-px size-3.5 shrink-0" />
        Les explications ont été rédigées à partir du plan lui-même : l'assistant n'a pas pu les formuler.
      </p>

      <p
        v-else-if="proposal.wording_status === 'skipped'"
        class="mt-2 flex items-start gap-1.5 text-xs leading-snug text-gray-dark/70"
      >
        <Info class="mt-px size-3.5 shrink-0" />
        Parcours recalculé sans les explications, pour répondre tout de suite à votre modification.
        Recalculez pour les obtenir.
      </p>
    </div>

    <div
      v-if="plan.constraints.length || plan.limits.length || separations.length || exclusions.length"
      class="rounded-lg bg-white p-4 text-xs leading-snug shadow"
    >
      <p class="mb-1.5 font-semibold uppercase tracking-wide text-gray-500">
        Ce qui a contraint ce parcours
      </p>
      <ul class="flex flex-col gap-1 text-gray-dark">
        <li v-for="constraint in plan.constraints" :key="constraint.rule_id" class="flex items-start gap-1.5">
          <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
          {{ constraint.label }}
        </li>
        <li v-for="limit in plan.limits" :key="limit" class="flex items-start gap-1.5 text-gray-dark/70">
          <Info class="mt-px size-3.5 shrink-0" />
          {{ limit }}
        </li>
        <li v-if="separations.length" class="text-gray-dark/70">
          Rendez-vous non regroupés : {{ separations.join(', ') }}.
        </li>
        <li v-if="exclusions.length" class="text-gray-dark/70">
          Jours écartés : {{ exclusions.join(', ') }}.
        </li>
      </ul>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto" :class="{'opacity-60': stale}">
      <HealthJourneyStepCard
        v-for="(step, index) in plan.steps"
        :key="step.client_ref"
        :step="step"
        :act-label="labelOf(step)"
        :program-name="actOf(step)?.programName ?? null"
        :reason-text="reasonOf(step)"
        :member-name="memberName(step.member_id)"
        :opens-visit="opensVisit(step, index)"
        :shares-visit="(visitSizes.get(step.visit) ?? 1) > 1"
        @edit="openPicker(step)"
        @unpin="emits('unpin', step.client_ref)"
        @remove="emits('remove', step.client_ref)"
      />
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-4 shadow">
      <p v-if="stale" class="flex min-w-0 items-start gap-1.5 text-xs leading-snug text-gray-dark">
        <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        Les programmes choisis ont changé : ce parcours est en cours de recalcul et ne peut pas être
        programmé en l'état.
      </p>
      <p v-else class="flex min-w-0 items-start gap-1.5 text-xs leading-snug text-gray-500">
        <MessageSquare class="mt-px size-3.5 shrink-0" />
        {{ recapNote }}
      </p>
      <Button
        class="bg-primary"
        :disabled="blocked || programming || generating"
        @click="emits('submit')"
      >
        Programmer le parcours
      </Button>
    </div>

    <HealthJourneySlotPicker
      v-if="editing"
      v-model:open="pickerOpen"
      :step="editing"
      :act-label="labelOf(editing)"
      @pick="pick"
    />
  </section>
</template>
